// Supabase-Auth im Browser – nur im Modus supabase (AUFTRAG §3). Das Paket wird erst dann geladen,
// damit local/demo es nicht im Bundle tragen. Der Server prüft das Access-Token (Bearer).
import type { AuthConfig } from '@bar/contracts/core';
import type { SupabaseClient } from '@supabase/supabase-js';

export type OAuthProvider = 'google' | 'azure';

let clientPromise: Promise<SupabaseClient> | undefined;

/** Supabase-Client (einmal je Seite) aus der Server-Konfiguration; wirft außerhalb des Modus supabase. */
export function getSupabase(config: AuthConfig): Promise<SupabaseClient> {
  if (config.mode !== 'supabase' || !config.supabase) {
    return Promise.reject(new Error('Supabase auth is not configured for this installation'));
  }
  const { url, anonKey } = config.supabase;
  clientPromise ??= import('@supabase/supabase-js').then(({ createClient }) =>
    createClient(url, anonKey, {
      auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true },
    }),
  );
  return clientPromise;
}

/** Aktuelles Access-Token oder undefined (für den API-Client). */
export async function supabaseAccessToken(config: AuthConfig): Promise<string | undefined> {
  const client = await getSupabase(config);
  const { data } = await client.auth.getSession();
  return data.session?.access_token ?? undefined;
}

export async function supabaseSignInWithPassword(
  config: AuthConfig,
  email: string,
  password: string,
) {
  const client = await getSupabase(config);
  const { error } = await client.auth.signInWithPassword({ email, password });
  if (error) throw new Error(error.message);
}

/** Magic Link an die E-Mail; nach dem Klick landet die Person auf `redirectTo`. */
export async function supabaseMagicLink(config: AuthConfig, email: string, redirectTo: string) {
  const client = await getSupabase(config);
  const { error } = await client.auth.signInWithOtp({
    email,
    options: { emailRedirectTo: redirectTo, shouldCreateUser: false },
  });
  if (error) throw new Error(error.message);
}

/**
 * OAuth (Google oder Microsoft/azure, wie in Supabase eingerichtet). Ist genau eine Firmen-Domain
 * erlaubt, bekommt Google den Hinweis `hd` (Kontoauswahl nur für diese Domain; prüfen muss trotzdem
 * der Server).
 */
export async function supabaseOAuth(
  config: AuthConfig,
  provider: OAuthProvider,
  redirectTo: string,
) {
  const client = await getSupabase(config);
  const [domain, ...more] = config.allowedEmailDomains ?? [];
  const queryParams =
    provider === 'google' && domain && more.length === 0 ? { hd: domain } : undefined;
  const { error } = await client.auth.signInWithOAuth({
    provider,
    options: { redirectTo, ...(queryParams ? { queryParams } : {}) },
  });
  if (error) throw new Error(error.message);
}

export async function supabaseResetPassword(config: AuthConfig, email: string, redirectTo: string) {
  const client = await getSupabase(config);
  const { error } = await client.auth.resetPasswordForEmail(email, { redirectTo });
  if (error) throw new Error(error.message);
}

export async function supabaseSignOut(config: AuthConfig) {
  const client = await getSupabase(config);
  await client.auth.signOut();
}

/** Ruft `onChange` bei An-/Abmeldung und Token-Erneuerung; liefert die Abmeldefunktion. */
export async function onSupabaseAuthChange(
  config: AuthConfig,
  onChange: () => void,
): Promise<() => void> {
  const client = await getSupabase(config);
  const { data } = client.auth.onAuthStateChange(() => onChange());
  return () => data.subscription.unsubscribe();
}
